import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭' },
] as const;

const SelectContainer = styled('div')({
  position: 'relative',
  width: '160px',
});

const SelectButton = styled('button')({
  width: '100%',
  padding: '10px 16px',
  borderRadius: '16px',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 500,
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    background: 'rgba(157, 78, 221, 0.1)',
  },
});

const DropdownContainer = styled('div')<{ open: boolean }>(({ open }) => ({
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  width: '100%',
  background: 'rgba(20, 20, 30, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '12px',
  overflow: 'hidden',
  opacity: open ? 1 : 0,
  visibility: open ? 'visible' : 'hidden',
  transform: open ? 'translateY(0)' : 'translateY(-10px)',
  transition: 'all 0.3s ease',
  zIndex: 1000,
}));

const Option = styled('button')<{ isSelected: boolean }>(({ isSelected }) => ({
  width: '100%',
  padding: '12px 16px',
  border: 'none',
  background: isSelected ? 'rgba(157, 78, 221, 0.1)' : 'transparent',
  color: '#fff',
  fontSize: '0.95rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(157, 78, 221, 0.15)',
  },
}));

const Arrow = styled('span')<{ open: boolean }>(({ open }) => ({
  borderStyle: 'solid',
  borderWidth: '2px 2px 0 0',
  display: 'inline-block',
  padding: '3px',
  transform: open ? 'rotate(-45deg) translateY(2px)' : 'rotate(135deg) translateY(-2px)',
  transition: 'transform 0.3s ease',
}));

interface LanguageSelectorProps {
  currentLang: 'en' | 'ru' | 'th';
  onChangeLang: (lang: 'en' | 'ru' | 'th') => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ currentLang, onChangeLang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentLanguage = languages.find(lang => lang.code === currentLang);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: 'en' | 'ru' | 'th') => {
    onChangeLang(code);
    setIsOpen(false);
  };

  return (
    <SelectContainer ref={containerRef}>
      <SelectButton onClick={() => setIsOpen(!isOpen)}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{currentLanguage?.flag}</span>
          <span>{currentLanguage?.label}</span>
        </span>
        <Arrow open={isOpen} />
      </SelectButton>

      <DropdownContainer open={isOpen}>
        {languages.map((language) => (
          <Option
            key={language.code}
            isSelected={currentLang === language.code}
            onClick={() => handleSelect(language.code)}
          >
            <span>{language.flag}</span>
            <span>{language.label}</span>
          </Option>
        ))}
      </DropdownContainer>
    </SelectContainer>
  );
};

export default LanguageSelector; 